/**
 * The league cards grouped by sport, each one a toggle with a check when it
 * is followed. Shared by onboarding's leagues step and the Sports you follow
 * screen on Account (ISSUE-1400), so the two can never look different.
 */
import type { ReactNode } from "react";

import type { typeOnboardingLeagueGroup } from "../onboarding/onboardingFollowsModel";
import { COMING_SOON_LABEL } from "../scores/leagueSelectorContract";

export type typeLeagueToggleGroupsProps = {
  groups: typeOnboardingLeagueGroup[];
  selectedLeagueIds: readonly string[];
  onToggle: (leagueId: string) => void;
  /** Each card's test id is this prefix plus the league key. */
  testIdPrefix: string;
  /** Read after each group's sport name, for a screen reader. */
  groupLabelSuffix: string;
  /** One quiet line under each group that has something to pick, or none. */
  groupHelp?: string | undefined;
};

export const LeagueToggleGroups = ({
  groups,
  selectedLeagueIds,
  onToggle,
  testIdPrefix,
  groupLabelSuffix,
  groupHelp,
}: typeLeagueToggleGroupsProps): ReactNode =>
  groups.map((group) => (
    <div key={group.sportId}>
      <p className="text-caption text-ink-secondary">{group.heading}</p>
      <div
        role="group"
        aria-label={`${group.heading} ${groupLabelSuffix}`}
        className="grid grid-cols-2 gap-3 lg:grid-cols-3"
      >
        {group.leagues.map((league) => {
          const isSelected = selectedLeagueIds.includes(league.leagueId);
          if (league.isComingSoon) {
            return (
              <button
                type="button"
                key={league.leagueId}
                disabled
                data-testid={`${testIdPrefix}${league.leagueKey}`}
                className="relative flex min-h-[56px] flex-col justify-center rounded-md border-2 border-hairline bg-surface py-2 pl-4 pr-12 text-left text-body-strong text-ink-tertiary"
              >
                {league.shortName}
                <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-hairline px-1 py-1 text-small text-ink-tertiary">
                  {COMING_SOON_LABEL}
                </span>
              </button>
            );
          }
          return (
            <button
              type="button"
              key={league.leagueId}
              data-testid={`${testIdPrefix}${league.leagueKey}`}
              aria-pressed={isSelected}
              onClick={() => {
                onToggle(league.leagueId);
              }}
              className={`relative flex min-h-[56px] flex-col justify-center rounded-md border-2 ${
                isSelected ? "border-primary" : "border-hairline"
              } bg-surface py-2 pl-4 pr-12 text-left text-body-strong text-ink`}
            >
              {league.shortName}
              {isSelected ? (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 grid h-5 w-5 place-items-center rounded-full bg-primary-solid text-on-primary">
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m5 13 4 4L19 7" />
                  </svg>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {groupHelp !== undefined && group.doShowGroupHelp ? (
        <p className="text-small text-ink-tertiary">{groupHelp}</p>
      ) : null}
    </div>
  ));
